import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { ArrowLeft, Package, MapPin, Clock, CheckCircle, Truck, XCircle, RotateCcw } from 'lucide-react';
import { describeLine } from '../lib/cartLine';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:   { label: 'Pending',   color: 'text-amber-600',  bg: 'bg-yellow-500/10 border-yellow-500/20', icon: Clock },
  paid:      { label: 'Paid',      color: 'text-blue-600',    bg: 'bg-blue-500/10 border-blue-500/20',     icon: CheckCircle },
  shipped:   { label: 'Shipped',   color: 'text-purple-600',  bg: 'bg-purple-500/10 border-purple-500/20', icon: Truck },
  delivered: { label: 'Delivered', color: 'text-green-600',   bg: 'bg-green-500/10 border-green-500/20',   icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'text-red-600',     bg: 'bg-red-500/10 border-red-500/20',       icon: XCircle },
  refunded:  { label: 'Refunded',  color: 'text-orange-600',  bg: 'bg-orange-500/10 border-orange-500/20', icon: RotateCcw },
};

const TIMELINE = ['pending', 'paid', 'processing', 'shipped', 'delivered'];

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/orders/${id}`, { withCredentials: true })
      .then(r => setOrder(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Order not found'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="store-page flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-honey border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="store-page relative">
      <Navbar />
      <div className="relative z-10 max-w-3xl mx-auto px-6 pt-8 pb-20">
        <button onClick={() => navigate('/orders')}
          className="flex items-center gap-2 text-ink/55 hover:text-ink text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Orders
        </button>

        {error ? (
          <div className="text-center py-20">
            <Package className="w-12 h-12 text-ink/30 mx-auto mb-4" />
            <p className="text-ink/55 text-lg">{error}</p>
          </div>
        ) : order ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            {/* Header */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <h1 className="text-2xl font-black text-ink mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Order {order.order_number}
                  </h1>
                  <p className="text-ink/55 text-sm">
                    Placed on {new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                {(() => {
                  const meta = STATUS_META[order.status] || STATUS_META.pending;
                  const Icon = meta.icon;
                  return (
                    <span data-testid="order-status-badge"
                      className={`flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-full border capitalize ${meta.bg} ${meta.color}`}>
                      <Icon className="w-4 h-4" /> {meta.label}
                    </span>
                  );
                })()}
              </div>

              {/* Status timeline (only for standard flow) */}
              {!['cancelled','refunded'].includes(order.status) && (
                <div className="mt-6 flex items-center gap-0">
                  {TIMELINE.map((s, i) => {
                    const done = TIMELINE.indexOf(order.status) >= i;
                    return (
                      <React.Fragment key={s}>
                        <div className="flex flex-col items-center gap-1.5">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${done ? 'bg-honey border-honey text-black' : 'bg-ink/[0.04] border-ink/10 text-ink/30'}`}>
                            {done ? <CheckCircle className="w-3.5 h-3.5" /> : i + 1}
                          </div>
                          <span className={`text-xs capitalize ${done ? 'text-honey-700' : 'text-ink/30'}`}>{s}</span>
                        </div>
                        {i < TIMELINE.length - 1 && (
                          <div className={`flex-1 h-0.5 mb-5 ${TIMELINE.indexOf(order.status) > i ? 'bg-honey/50' : 'bg-ink/[0.07]'}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {order.tracking_number && (
                <div className="mt-4 p-3 bg-honey/5 border border-honey/20 rounded-xl text-sm">
                  <span className="text-ink/60">Tracking{order.carrier ? ` (${order.carrier})` : ''}: </span>
                  <span className="text-honey-700 font-bold font-mono">{order.tracking_number}</span>
                  {order.tracking_url && (
                    <a href={order.tracking_url} target="_blank" rel="noreferrer" className="ml-3 text-honey-600 font-bold hover:underline">Track package →</a>
                  )}
                </div>
              )}
            </div>

            {/* Items */}
            <div className="glass-card rounded-2xl p-6">
              <h2 className="text-ink font-bold mb-4 flex items-center gap-2">
                <Package className="w-4 h-4 text-honey-700" /> Items Ordered
              </h2>
              <div className="space-y-3">
                {order.items?.map((item, i) => (
                  <div key={i} className="flex items-center gap-4" data-testid={`order-item-${i}`}>
                    <div className="w-14 h-14 bg-ink/[0.04] rounded-xl overflow-hidden flex-shrink-0 border border-ink/10">
                      {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-ink font-medium text-sm">{item.title}</p>
                      {describeLine(item) && <p className="text-ink/55 text-xs">{describeLine(item)}</p>}
                      <p className="text-ink/55 text-xs mt-0.5">x{item.quantity} · ${item.price?.toFixed(2)} each</p>
                    </div>
                    <p className="text-ink font-bold">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="mt-5 pt-4 border-t border-ink/10 space-y-2 text-sm">
                <div className="flex justify-between text-ink/60"><span>Subtotal</span><span>${order.subtotal?.toFixed(2)}</span></div>
                <div className="flex justify-between text-ink/60"><span>Tax</span><span>${order.tax?.toFixed(2)}</span></div>
                <div className="flex justify-between text-ink/60"><span>Shipping</span><span>{order.shipping === 0 ? <span className="text-green-600">Free</span> : `$${order.shipping?.toFixed(2)}`}</span></div>
                {order.discounts?.length ? order.discounts.map((d, i) => (
                  <div key={i} className="flex justify-between text-green-600"><span>{d.name}</span><span>-${d.amount.toFixed(2)}</span></div>
                )) : order.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount {order.coupon_code && <span className="text-xs font-mono">({order.coupon_code})</span>}</span>
                    <span>-${order.discount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base pt-2 border-t border-ink/10">
                  <span className="text-ink">Total</span>
                  <span className="text-honey-700">${order.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Shipping */}
            {order.shipping_address && (
              <div className="glass-card rounded-2xl p-6">
                <h2 className="text-ink font-bold mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-honey-700" /> Shipping Address
                </h2>
                <p className="text-ink/70 text-sm leading-relaxed">
                  {order.shipping_address.name}<br />
                  {order.shipping_address.line1}<br />
                  {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}<br />
                  {order.shipping_address.country}
                </p>
              </div>
            )}

            {/* Notes */}
            {order.notes && (
              <div className="glass-card rounded-2xl p-6">
                <h2 className="text-ink font-bold mb-2">Order Notes</h2>
                <p className="text-ink/60 text-sm">{order.notes}</p>
              </div>
            )}

            <div className="flex gap-4 pt-2">
              <Link to="/" className="btn-secondary text-sm px-5 py-2.5">Continue Shopping</Link>
              <Link to="/orders" className="btn-primary text-sm px-5 py-2.5">All Orders</Link>
            </div>
          </motion.div>
        ) : null}
      </div>
    </div>
  );
}
